<template>
  <div>
    <div
      v-if="showModal"
      class="aa-wizard-overlay"
    >
      <div
        ref="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aa-wizard-title"
        tabindex="-1"
        class="aa-wizard-panel"
      >
        <!-- The app's red, which is what identifies this product: the navbar's
             Upload Art button is `bg-red-600` (DesktopNavbar.vue:115), and the
             two dialogs nearest this one outline their secondary action in the
             same `red-600` (ArtworkFlagDialog.vue:38, UploadPhoto.vue:41).
             Tailwind resolves red-600 to #dc2626, the accent-fill the design
             system already names.

             `color` feeds progressBarStyle AND fillButtonStyle in the library
             (FormWizard.vue:202-215), but the footer buttons here are native
             and take their fill from --aa-accent-fill, so this paints the
             active step circle and the steps already finished. -->
        <form-wizard
          :class="['aa-wizard', 'aa-wizard--step-' + step]"
          color="#dc2626"
          stepSize="sm"
          subtitle=""
        >
          <template v-slot:title>
            <div class="aa-wizard-header">
              <h2 id="aa-wizard-title" class="aa-wizard-heading">Upload an Artwork</h2>
              <button
                type="button"
                class="aa-wizard-close"
                aria-label="Close the upload form"
                @click="close()"
              >
                <!-- The glyph is decoration; the button's name is on the
                     button. A bare "X" character reads as the letter X to a
                     screen reader, which is what it was before. -->
                <svg
                  viewBox="0 0 20 20"
                  width="20"
                  height="20"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M4 4 L16 16 M16 4 L4 16"
                    stroke="currentColor"
                    stroke-width="1.5"
                    fill="none"
                    stroke-linecap="square"
                  />
                </svg>
              </button>
            </div>
          </template>

          <tab-content title="Photo">
            <div class="aa-wizard-step">
              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="art-photo">
                  Photo of the artwork
                  <span class="aa-wizard-required" aria-hidden="true">*</span>
                </label>
                <input
                  type="file"
                  id="art-photo"
                  name="photo"
                  accept="image/*"
                  required
                  class="aa-wizard-file"
                  @change="handleFileUpload"
                />
              </div>
              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="photo-credit">
                  Photo credit
                </label>
                <input
                  type="text"
                  name="attribution_text"
                  id="photo-credit"
                  autocomplete="name"
                  placeholder="e.g. Jane Doe"
                  v-model="photo"
                  class="aa-wizard-field"
                />
                <p class="aa-wizard-help">Who took the photo. Left blank, it is credited to Anonymous.</p>
              </div>
              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="photographer-url">
                  Photographer URL
                </label>
                <input
                  type="url"
                  inputmode="url"
                  name="attribution_url"
                  id="photographer-url"
                  autocomplete="url"
                  spellcheck="false"
                  placeholder="https://example.com/portfolio"
                  v-model="url"
                  class="aa-wizard-field"
                />
              </div>
              <div class="aa-wizard-preview" v-if="image">
                <img
                  :src="imageDefault || image"
                  :alt="previewAlt"
                  width="240"
                  height="240"
                  class="aa-wizard-preview-image"
                />
              </div>
            </div>
          </tab-content>

          <tab-content title="Tag">
            <div class="aa-wizard-step">
              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="art-categories">
                  Categories
                  <span class="aa-wizard-required" aria-hidden="true">*</span>
                </label>
                <v-select
                  input-id="art-categories"
                  class="aa-wizard-select"
                  placeholder="Select or press Enter"
                  multiple
                  :select-on-key-codes="[188, 13]"
                  select-on-tab
                  v-model="art.categories"
                  label="name"
                  :options="artCategories"
                  :reduce="category => category.name"
                />
              </div>
              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="art-tags">
                  Descriptive tags
                  <span class="aa-wizard-required" aria-hidden="true">*</span>
                </label>
                <v-select
                  input-id="art-tags"
                  class="aa-wizard-select"
                  placeholder="Select or press Enter"
                  :select-on-key-codes="[188, 13]"
                  select-on-tab
                  multiple
                  v-model="art.tags"
                  label="name"
                  :options="artDescriptiveTags"
                  :reduce="tag => tag.name"
                />
              </div>
              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="art-title">
                  Title of artwork
                  <span class="aa-wizard-required" aria-hidden="true">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  id="art-title"
                  autocomplete="off"
                  placeholder="e.g. Greetings from Chicago"
                  v-model="art.title"
                  required
                  class="aa-wizard-field"
                />
              </div>
              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="art-artists">
                  Artist(s)
                  <span class="aa-wizard-required" aria-hidden="true">*</span>
                </label>
                <v-select
                  input-id="art-artists"
                  class="aa-wizard-select"
                  placeholder="Select, or type a name and press Enter to add"
                  :select-on-key-codes="[188, 13]"
                  select-on-tab
                  taggable
                  multiple
                  v-model="art.artists"
                  label="name"
                  :options="artistsList"
                  @option:created="createArtist"
                  :reduce="artist => artist.id"
                />
              </div>
            </div>
          </tab-content>

          <tab-content title="Describe">
            <div class="aa-wizard-step">
              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="art-city">City</label>
                <input
                  type="text"
                  name="city"
                  id="art-city"
                  autocomplete="address-level2"
                  placeholder="e.g. Chicago"
                  v-model="art.city"
                  class="aa-wizard-field"
                />
              </div>
              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="art-year">Year</label>
                <input
                  type="text"
                  name="year"
                  id="art-year"
                  autocomplete="off"
                  spellcheck="false"
                  placeholder="e.g. 2019"
                  v-model="art.year"
                  class="aa-wizard-field"
                />
                <p class="aa-wizard-help">The year it went up, as far as you know.</p>
              </div>
              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="art-website">Website</label>
                <input
                  type="url"
                  inputmode="url"
                  name="website"
                  id="art-website"
                  autocomplete="off"
                  spellcheck="false"
                  placeholder="https://example.com"
                  v-model="art.website"
                  class="aa-wizard-field"
                />
              </div>
              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="art-description">Description</label>
                <textarea
                  id="art-description"
                  name="description"
                  rows="4"
                  placeholder="What does it show, and what is it about?"
                  v-model="art.description"
                  class="aa-wizard-field aa-wizard-textarea"
                ></textarea>
              </div>
            </div>
          </tab-content>

          <tab-content title="Location">
            <div class="aa-wizard-step">
              <!-- The one Google map left in the app. Every other map is
                   Leaflet after PR #130, and this one costs a Google Maps
                   Dynamic Maps load every time the wizard opens -- a trade the
                   owner accepted for this screen alone, because a draggable
                   pin plus Places suggestions plus reverse geocoding are all
                   the same SDK, and rebuilding them on Leaflet would mean
                   three separate services and a second geocoding key.
                   LocationMap.vue (Leaflet) still serves the artwork page. -->
              <GmapMap
                ref="locationMap"
                class="aa-wizard-map"
                :center="item.center"
                :zoom="16"
                :options="mapOptions"
                @click="handleMapClick"
              >
                <GmapMarker
                  :position="item.position"
                  :draggable="true"
                  :icon="markerIcon"
                  title="Drag to move the artwork's pin"
                  @dragend="handleMarkerDrag"
                />
              </GmapMap>

              <p class="aa-wizard-help" id="art-location-help">
                Type an address and pick a suggestion, or drag the pin. Tapping the map moves the
                pin too.
              </p>

              <!-- Not an alert and not red: the design system puts results and
                   failures inline, in the place the result would have been.
                   Polite so a reverse-geocode landing mid-typing waits for a
                   pause rather than interrupting (WCAG 4.1.3). -->
              <p
                class="aa-wizard-note"
                :class="{ 'aa-wizard-note--filled': !!locationStatus }"
                aria-live="polite"
              >
                <svg
                  v-if="locationStatus"
                  class="aa-wizard-note-icon"
                  viewBox="0 0 20 20"
                  width="20"
                  height="20"
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle cx="10" cy="10" r="7.25" fill="none" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M10 9v5M10 6.2v.1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                <span>{{ locationStatus }}</span>
              </p>

              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="art-location">
                  Address
                  <span class="aa-wizard-required" aria-hidden="true">*</span>
                </label>
                <places-autocomplete
                  id="art-location"
                  name="address"
                  describedby="art-location-help"
                  placeholder="Start typing an address…"
                  classes="aa-wizard-field"
                  v-model="art.address"
                  :fields="[
                    'geometry',
                    'name',
                    'formatted_address',
                    'address_components',
                    'types',
                    'vicinity'
                    ]"
                  @placeChange="handleArtLocation"
                />
              </div>
              <div class="aa-wizard-row">
                <label class="aa-wizard-label" for="art-location-description">
                  Address description
                </label>
                <textarea
                  id="art-location-description"
                  name="location_description"
                  rows="3"
                  placeholder="e.g. On the north wall, facing the alley"
                  v-model="art.location_description"
                  class="aa-wizard-field aa-wizard-textarea"
                ></textarea>
              </div>
            </div>
          </tab-content>

          <template slot="footer" slot-scope="props">
            <div class="aa-wizard-footer">
              <!-- Always rendered, so a screen reader has a live region to
                   announce into when the list changes; a v-if would create the
                   region and its content in the same patch, which readers
                   routinely miss. -->
              <p
                class="aa-wizard-note"
                :class="{ 'aa-wizard-note--filled': missing.length > 0 }"
                aria-live="polite"
              >
                <template v-if="missing.length">
                  <svg
                    class="aa-wizard-note-icon"
                    viewBox="0 0 20 20"
                    width="20"
                    height="20"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <circle cx="10" cy="10" r="7.25" fill="none" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M10 6v5M10 13.7v.1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  <span>Still needed before you can continue: {{ missingSentence }}.</span>
                </template>
              </p>

              <div class="aa-wizard-actions">
                <!-- Native buttons, not vue-form-wizard's <wizard-button>: that
                     component hard-codes tabindex="-1" (WizardButton.vue:2) and
                     the library only makes it reachable by wrapping it in a
                     <span role="button" tabindex="0"> in its own default
                     footer. This footer replaces that default, so every control
                     here was out of the tab order -- the wizard could not be
                     completed with a keyboard at all. -->
                <button
                  v-if="step === 1"
                  type="button"
                  class="aa-wizard-button aa-wizard-button--quiet"
                  @click="close()"
                >Cancel</button>
                <button
                  v-if="step > 1"
                  type="button"
                  class="aa-wizard-button aa-wizard-button--quiet"
                  @click="props.prevTab(); step--"
                >Previous</button>

                <button
                  v-if="!props.isLastStep"
                  type="button"
                  class="aa-wizard-button aa-wizard-button--primary"
                  :disabled="isDisabled"
                  @click="props.nextTab(); step++"
                >Next</button>
                <button
                  v-else
                  type="button"
                  class="aa-wizard-button aa-wizard-button--primary"
                  :disabled="isDisabled || loading"
                  @click="handleSubmit"
                >{{ loading ? "Submitting…" : "Submit" }}</button>
              </div>
            </div>
          </template>
        </form-wizard>
      </div>
    </div>
    <!-- Presentational: it is the modal's shadow, and every action it offers
         (dismiss) is on the close button and on Escape. Marked aria-hidden so
         it is not announced as a control that a keyboard cannot reach. -->
    <div
      v-if="showModal"
      class="aa-wizard-backdrop"
      aria-hidden="true"
      @click="close()"
    ></div>
  </div>
</template>

<script>
  import axios from "axios";
  import EXIF from 'exif-js'
  import {FormWizard, TabContent} from "vue-form-wizard";
  import "vue-form-wizard/dist/vue-form-wizard.min.css";
  import PlaceHolderImage from "../assets/placeholder.png";
  import ArtAroundPin from "../assets/artaround-pin.png";
  import PlacesAutocomplete from "./PlacesAutocomplete.vue";
  import {getLatLngImage} from "../utils";
  import {getCurrentPosition} from "../features/geolocation";
  import {pickGeocodedAddress, toLatLngLiteral} from "../features/arts/wizard-location";
  import {missingRequirements} from "../features/arts/wizard-requirements";

  // Elements that take keyboard focus, for the focus trap. `[tabindex]` is
  // included for vue-form-wizard's step circles, which are focusable divs
  // (WizardStep.vue:7), and excludes -1 so the dialog container itself -- the
  // element focus is moved TO on open -- does not become a trap stop.
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  export default {
    components: {
      FormWizard,
      TabContent,
      PlacesAutocomplete,
    },
    props: ["showModal"],
    name: "ArtworkWizardUploader",
    mounted: function () {
      // Whoever opened the wizard: the navbar's "Upload Art" button, almost
      // always. Focus has to go back there on close, or a keyboard visitor is
      // returned to the top of the document with no memory of where they were
      // (WCAG 2.4.3).
      this.openerElement = document.activeElement;

      // Escape and the Tab trap are bound on the document rather than on the
      // dialog, because the dialog can lose focus in ways this component does
      // not see -- a click on the backdrop, a click on the Places dropdown,
      // which Google appends to document.body and not inside this subtree.
      document.addEventListener('keydown', this.onKeydown, true);

      // The page behind is the map; without this it scrolls under the modal
      // whenever the modal itself has nothing left to scroll. The previous
      // inline value is restored rather than assumed to be '' -- nothing else
      // sets it today, but a modal that permanently disables page scrolling if
      // it is ever nested is a worse bug than the one being fixed.
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      this.$nextTick(function () {
        // Announces the dialog and its name, and puts the next Tab inside it,
        // without dropping the visitor straight onto the file picker.
        if (this.$refs.dialog) this.$refs.dialog.focus();

        this.fetchCategories();
        this.fetchDescriptiveTags();
        this.fetchArtistsList();
        this.centerMap();

        // The pin's own artwork, sized. `scaledSize` must be a
        // google.maps.Size, which does not exist until the loader has run, so
        // the icon is built after the API resolves rather than in `data`. The
        // marker renders with Google's default pin for the moment in between,
        // and vue2-google-maps' `icon` prop is twoWay (marker.js:34), so
        // assigning it later calls setIcon on the live marker.
        //
        // 30x45 matches what the Leaflet version drew (LocationMap.vue:93);
        // the source PNG is 54x86, which at natural size is a pin taller than
        // a fifth of the map.
        if (typeof this.$gmapApiPromiseLazy === 'function') {
          this.$gmapApiPromiseLazy().then(() => {
            this.markerIcon = {
              url: ArtAroundPin,
              scaledSize: new window.google.maps.Size(30, 45),
              // Bottom centre: the tip of the pin is the coordinate it claims.
              anchor: new window.google.maps.Point(15, 45),
            };
          }).catch(() => {
            // Handled where it is visible: the map simply does not appear and
            // the address field stays a plain text box. Nothing here can fix a
            // blocked or rejected loader.
          });
        }
      });
    },
    beforeDestroy() {
      document.removeEventListener('keydown', this.onKeydown, true);
      document.body.style.overflow = this.previousBodyOverflow || '';

      // isConnected guards the case where the opener was itself removed while
      // the wizard was up -- focusing a detached node silently sends focus to
      // <body>, which is the outcome this is trying to avoid.
      if (this.openerElement && this.openerElement.isConnected) {
        this.openerElement.focus();
      }
    },
    data() {
      return {
        file: {},
        image: "",
        imageDefault: "",
        fileName: "",
        photo: "",
        url: "",
        step: 1,
        markerIcon: null,
        // Said out loud after the pin moves, and shown inline in the place the
        // answer would have been. Empty most of the time.
        locationStatus: "",
        art: {
          categories: [],
          tags: [],
          title: '',
          artists: [],
          year: '',
          city: '',
          website: '',
          description: '',
          location_description: '',
          address: '',
          lat: 0,
          lng: 0,
        },
        defaultArt: {
          categories: [],
          tags: [],
          title: '',
          artists: [],
          year: '',
          city: '',
          website: '',
          description: '',
          location_description: '',
          address: '',
          lat: 0,
          lng: 0,
        },
        artCategories: [],
        artDescriptiveTags: [],
        artistsList: [],
        item: {
          // Whether a coordinate has been ASSERTED -- by the photo's EXIF, by a
          // Places selection, or by the visitor moving the pin. Deliberately
          // not set by centerMap(): the browser's idea of where the visitor is
          // standing is a starting view, not a claim about where the artwork
          // is, and treating it as one would file pieces at whichever desk the
          // uploader happened to be sitting at.
          haveLatLng: false,
          center: {
            lat: 41.964670069207244,
            lng: -87.68149129003832
          },
          position: {
            lat: 41.964670069207244,
            lng: -87.68149129003832
          }
        },
        loading: false,
      }
    },
    methods: {
      // The single place the artwork's coordinates are written. Everything that
      // can move the pin -- Places, a map click, a marker drag, the photo's
      // EXIF -- goes through here, so `art.lat`/`art.lng` (what handleSubmit
      // posts) and `item.position` (what the marker draws) cannot drift apart.
      //
      // `recenter` is false for the gestures the visitor performs ON the map.
      // vue2-google-maps watches `center` and calls setCenter on any change
      // (mapImpl.js:163-167), so recentring after a drag would yank the map out
      // from under the finger that just finished dragging. A Places selection
      // is the opposite case: it names somewhere else entirely, and not moving
      // the view would leave the pin off screen.
      applyLatLng(latLng, {recenter = false} = {}) {
        const point = toLatLngLiteral(latLng);
        if (!point) return null;

        this.item.position = {lat: point.lat, lng: point.lng};
        if (recenter) this.item.center = {lat: point.lat, lng: point.lng};
        this.item.haveLatLng = true;
        this.art.lat = point.lat;
        this.art.lng = point.lng;

        return point;
      },
      handleArtLocation(place) {
        const point = this.applyLatLng(place && place.geometry && place.geometry.location, {
          recenter: true,
        });

        if (!point) {
          // A `place_changed` with no geometry is what Places emits when the
          // visitor presses Enter on text that matched no suggestion. Saying so
          // is the difference between "nothing happened" and "the pin did not
          // move, and here is why".
          this.locationStatus = 'That address did not match a place. Pick a suggestion from the list, or move the pin.';
          return;
        }

        if (place.formatted_address) this.art.address = place.formatted_address;
        this.locationStatus = "";
      },
      handleMapClick(event) {
        // Tapping the map is the single-pointer alternative to dragging the
        // pin, which WCAG 2.5.7 requires and which a 30x45 target on a phone
        // badly needs. The keyboard route to the same outcome is the address
        // field above (2.1.1) -- Google's map has no keyboard gesture that
        // places a marker.
        this.moveTo(event && event.latLng);
      },
      handleMarkerDrag(event) {
        this.moveTo(event && event.latLng);
      },
      // Both map gestures land here: place the pin, then tell the visitor what
      // is now underneath it, because the Address field above is about to be
      // overwritten and a text field that silently changes is worse than one
      // that disagrees.
      moveTo(latLng) {
        const point = this.applyLatLng(latLng);
        if (!point) return;

        this.locationStatus = 'Finding the address for the new pin…';

        this.reverseGeocode(point).then((address) => {
          if (address) {
            this.art.address = address;
            this.locationStatus = "";
            return;
          }

          // No street address exists for this point -- an alley, a park, open
          // water. The old text described the OLD pin, so keeping it would be
          // the exact silent disagreement this is here to prevent; and the
          // submission is still complete, because the coordinates are what the
          // map and the Art model consume.
          this.art.address = "";
          this.locationStatus = 'No street address here. The pin’s position is what gets saved.';
        });
      },
      // Reverse geocoding through the Maps JS SDK rather than through
      // features/geolocation's getAddressFromLatLng, which calls the Geocoding
      // REST API directly. That REST call sends the same browser key, and a
      // browser key is HTTP-referer restricted, which is right for a script tag
      // and wrong for a cross-origin XHR -- it is a second code path with a
      // second failure mode for an answer the SDK already on this page gives.
      //
      // Always resolves, never rejects: every caller's next move is the same
      // whether the geocoder said ZERO_RESULTS, refused the key, or was never
      // loaded at all.
      reverseGeocode(latLng) {
        return new Promise((resolve) => {
          if (!window.google || !window.google.maps) return resolve("");

          try {
            new window.google.maps.Geocoder().geocode({location: latLng}, (results) => {
              resolve(pickGeocodedAddress(results));
            });
          } catch (_e) {
            resolve("");
          }
        });
      },
      onKeydown(event) {
        if (!this.showModal || !this.$refs.dialog) return;

        // 'Esc' is IE/legacy Edge's spelling; harmless to accept.
        if (event.key === 'Escape' || event.key === 'Esc') {
          // Escape belongs to the innermost thing that is open. vue-select and
          // Google Places both use it to dismiss their suggestion list, and
          // closing the whole wizard instead would throw away everything typed
          // so far because someone dismissed a dropdown.
          if (this.escapeBelongsToAnOpenList(event.target)) return;
          event.stopPropagation();
          this.close();
          return;
        }

        if (event.key !== 'Tab') return;
        this.trapTab(event);
      },
      escapeBelongsToAnOpenList(target) {
        if (target && target.closest && target.closest('.v-select.vs--open')) return true;

        // Places' dropdown is appended to document.body, so it cannot be found
        // by walking up from the input. Ask whether one is on screen instead.
        // Google toggles `display` on the container; offsetParent is null while
        // it is hidden.
        const pac = document.querySelector('.pac-container');
        return !!(pac && pac.offsetParent !== null);
      },
      trapTab(event) {
        const dialog = this.$refs.dialog;
        // Inactive wizard steps are `v-show`n (TabContent.vue:2), so their
        // fields are display:none and unreachable; offsetParent is null for
        // exactly those, and keeping them would send Tab to a hidden step.
        const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE))
          .filter((el) => el.offsetParent !== null);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        const inside = dialog.contains(active);

        if (event.shiftKey && (active === first || !inside)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || !inside)) {
          event.preventDefault();
          first.focus();
        }
      },
      close() {
        this.$emit('close');
      },
      reset() {
        this.step = 1;
        this.photo = "";
        this.url = "";
        this.file = {};
        this.image = "";
        this.imageDefault = "";
        this.fileName = "";
        this.locationStatus = "";

        this.art = {...{},...this.defaultArt};

        this.item = {
          // Was omitted here, which left it `undefined` after a reset while the
          // location step still read it as a boolean.
          haveLatLng: false,
          center: {
            lat: 41.964670069207244,
            lng: -87.68149129003832
          },
          position: {
            lat: 41.964670069207244,
            lng: -87.68149129003832
          }
        };
        this.loading = false;
      },
      handleFileUpload({ target: { files } }) {
        const [file] = files;
        if (!file) return;

        this.fileName = file.name;
        this.file = file;

        const reader = new FileReader();
        const component = this;

        EXIF.getData(file, function (){
          // `this` is EXIF's own wrapper around the file; the component is
          // captured above because getData rebinds it.
          const latLng = getLatLngImage(this)

          if (!latLng.haveError){
            // The photo says where it was taken, which is a claim about the
            // artwork and not about the uploader -- so it recentres.
            component.applyLatLng(latLng, {recenter: true});
            component.reverseGeocode(latLng).then((address) => {
              if (address) component.art.address = address;
            });
            return;
          }

          getCurrentPosition().then((position) => {
            const here = {lat: position.coords.latitude, lng: position.coords.longitude};
            // The browser's fix moves the VIEW only. It is where the visitor
            // is standing, which is a good guess and not an assertion, so it
            // must not satisfy the location step on its own -- see
            // item.haveLatLng.
            component.item.center = here;
            component.item.position = here;
          }).catch((e) => { console.error(e) })
        });

        reader.onload = e => {
          if (e.target.result){
            // HEIC has no browser decoder, so the data URL renders as a broken
            // image; the placeholder stands in until the server converts it.
            this.imageDefault = e.target.result.includes('image/heic') ? PlaceHolderImage : "";
          }
          this.image = e.target.result;
        };

        reader.readAsDataURL(file);
      },
      async fetchCategories() {
        this.artCategories = await axios
          .get("/api/categories.json")
          .then(res => res.data);
      },
      async fetchDescriptiveTags() {
        this.artDescriptiveTags = await axios
          .get("/api/tags.json")
          .then(res => res.data);
      },
      async createArtist(artist) {
        const createdArtist = await axios.post('/api/artists.json', artist).then(res => res.data)
        this.art.artists = this.art.artists.filter(artist => !!artist)
        this.artistsList.push(createdArtist);
        this.art.artists.push(createdArtist.id);
      },
      async fetchArtistsList() {
        this.artistsList = await axios.get("/api/artists.json").then(res => res.data);
      },
      centerMap(){
        if (!navigator.geolocation) return;

        navigator.geolocation.getCurrentPosition(pos => {
          const {coords: {latitude, longitude}} = pos;
          // A view, not a claim: haveLatLng stays false. Skipped once the
          // photo's EXIF or the visitor has already fixed a point, because a
          // late permission grant must not drag the pin off it.
          if (this.item.haveLatLng) return;
          this.item.center = {lat: latitude, lng: longitude};
          this.item.position = {lat: latitude, lng: longitude};
        }, () => {
          // Denied or unavailable. The map opens on the default Chicago view,
          // which is what it did before anyone was asked.
        });
      },
      async handleSubmit() {
        this.loading = true;

        const {artists, categories, tags, ...rest} = this.art;

        const createdArt = await axios.post('/api/arts.json', {
          ...rest,
          tag_list: tags.join(','),
          category_list: categories.join(','),
          art_artist_ids: artists
        }).then(res=>res.data);

        const formData = new FormData();
        formData.append("art_id", createdArt.id);
        formData.append("image", this.file, this.fileName);
        formData.append("attribution_text", this.photo || 'Anonymous');
        formData.append("attribution_url", this.url);
        formData.append("primary", "true");

        await axios.post('/api/photos.json', formData).then(res=>res.data)
        this.$emit("close");
        this.$router.push('/art/' + createdArt.friendly_id, ()=>{
          this.reset();
          this.$router.go(0);
        })
      },
    },
    computed: {
      mapOptions() {
        return {
          // The wizard is a form, not an atlas: Street View, satellite and
          // fullscreen are three ways to leave the one job this map has.
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          // Google's POI icons swallow a click and open their own info window,
          // so on the interesting half of any city block the map would look
          // like it was ignoring taps rather than moving the pin.
          clickableIcons: false,
          // Two fingers to pan, on purpose. This 320px map sits inside a modal
          // that scrolls, and 'greedy' would eat every vertical swipe that
          // started on it -- a scroll trap for anyone reaching the fields
          // below. Google draws its own "use two fingers" overlay for this.
          gestureHandling: 'cooperative',
        };
      },
      previewAlt() {
        return this.art.title
          ? `Preview of the photo you chose for ${this.art.title}`
          : 'Preview of the photo you chose';
      },
      missing() {
        return missingRequirements(this.step, {
          fileName: this.file && this.file.name,
          categories: this.art.categories,
          tags: this.art.tags,
          title: this.art.title,
          artists: this.art.artists,
          hasCoordinates: this.item.haveLatLng,
        });
      },
      missingSentence() {
        // Lower-cased after the first, so the list reads as one sentence rather
        // than four headlines.
        return this.missing
          .map((item, index) => (index === 0 ? item : item.charAt(0).toLowerCase() + item.slice(1)))
          .join(', ');
      },
      isDisabled() {
        return this.missing.length > 0;
      }
    },
    watch: {
      step(val) {
        if (val !== 4) return;

        // The location step is `v-show`n like every other tab (TabContent.vue:2),
        // so the Google map was built inside a display:none container and has
        // never had a size to lay tiles out in. Modern Maps JS observes its
        // container, but it keeps the top-left corner rather than the centre
        // when the box changes, which lands the pin off screen. Re-asserting the
        // centre once the tab is visible is the whole fix.
        this.$nextTick(() => {
          const map = this.$refs.locationMap;
          if (!map || !map.$mapPromise) return;
          map.$mapPromise.then((mapObject) => mapObject.setCenter(this.item.center));
        });

        // Fill the address in for a pin the visitor has not typed anything for
        // -- the EXIF path, mainly.
        if (this.item.haveLatLng && !this.art.address) {
          this.reverseGeocode(this.item.position).then((address) => {
            if (address) this.art.address = address;
          });
        }
      },
    }
  };
</script>

<style>
/* The wizard, in the site's design system.
 *
 * Not scoped, for the same reason MapComponent.vue keeps an unscoped block:
 * most of what has to be restyled here is markup vue-form-wizard renders
 * itself (.wizard-btn, .stepTitle, .wizard-icon-circle), which a scoped block
 * cannot reach without ::v-deep on every selector. Every rule is prefixed with
 * .aa-wizard or .aa-wizard-* so nothing leaks onto the rest of the app.
 *
 * Tokens are the measured site values. Two radii exist in this system -- 0 and
 * 999px -- and the wizard used Tailwind's rounded-md everywhere, which is
 * neither; everything below is square. */
.aa-wizard-overlay,
.aa-wizard-backdrop {
  --aa-ink: #12302e;
  --aa-ink-2: #5b5e5e;
  /* Defined for completeness and used nowhere. The system reserves ink-3 for
     11px caps labels and icons; measured against white it is 3.28:1, under the
     4.5:1 WCAG 1.4.3 asks of text at that size, so every label in here uses
     ink or ink-2 instead. */
  --aa-ink-3: #8a8f8f;
  --aa-accent-fill: #dc2626;
  --aa-surface: #f2f2f2;
  --aa-rule: #dddddd;
  --aa-rule-2: #eeeeee;
}

.aa-wizard-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  /* The wizard opens over whatever page is behind it, and those pages keep
     their own fixed layers: HomePage's Map/List pill is 1100 and its filters
     sheet 1201 (both sit above Leaflet's controls at 1000). A modal has to
     outrank all of them, or they paint over the form on phones. */
  z-index: 1300;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-x: hidden;
  overflow-y: auto;
  /* Keeps a rubber-band scroll at the end of the modal from continuing into
     the map behind it. */
  overscroll-behavior: contain;
  /* Full-bleed on a phone, so the panel has to clear the notch and the home
     indicator itself. */
  padding: max(24px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right))
    max(24px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
}

@media (min-width: 640px) {
  .aa-wizard-overlay {
    align-items: center;
  }
}

.aa-wizard-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1290;
  background: rgba(18, 48, 46, 0.45);
}

.aa-wizard-panel {
  width: 100%;
  max-width: 1024px;
  background: #ffffff;
  border-radius: 0;
  /* The system's single elevation, for the things that float. */
  box-shadow: 0 1px 2px rgba(18, 48, 46, 0.08), 0 8px 24px rgba(18, 48, 46, 0.1);
  padding: 24px;
  color: var(--aa-ink);
  font: 400 14px/21px "Open Sans", "Helvetica", arial, sans-serif;
}

/* The dialog container takes focus on open so the dialog and its name are
   announced. It is not a control, so it gets no ring of its own. */
.aa-wizard-panel:focus {
  outline: none;
}

.aa-wizard-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--aa-rule-2);
  text-align: left;
}

.aa-wizard-heading {
  margin: 0;
  font: 700 20px/24px "Roboto Condensed", "Arial Narrow", Arial, sans-serif;
  color: var(--aa-ink);
  text-wrap: balance;
}

.aa-wizard-close {
  flex: none;
  /* 44x44 rather than the 24x24 the guideline floors at: this control is the
     only pointer route out of a modal that covers the page. */
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 0;
  background: transparent;
  color: var(--aa-ink);
  cursor: pointer;
  touch-action: manipulation;
  transition: background-color 120ms ease, border-color 120ms ease;
}

.aa-wizard-close:hover {
  background: var(--aa-surface);
  border-color: var(--aa-rule);
}

.aa-wizard-close:focus-visible {
  outline: 2px solid var(--aa-accent-fill);
  outline-offset: 2px;
}

/* vue-form-wizard's own chrome. */
.aa-wizard .wizard-header {
  padding: 0;
}

.aa-wizard .stepTitle {
  margin-top: 8px;
  font: 400 11px/16px "Open Sans", "Helvetica", arial, sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  /* The system assigns ink-3 (#8a8f8f) to 11px caps labels, but these are the
     step names -- the only text saying where you are in a four-step form -- and
     ink-3 measures 3.28:1, under the 4.5:1 WCAG 1.4.3 asks of text this size.
     The shape is the system's; the colour is one step darker so it can be
     read. */
  color: var(--aa-ink-2);
}

.aa-wizard .stepTitle.active {
  color: var(--aa-ink);
}

/* Circles, and nothing around them.
 *
 * These were square for a while, on the reading that the design system's
 * "corners stay square" covers everything. It does not read that way on
 * screen: the system's square corners are for surfaces that hold content --
 * cards, fields, the panel -- and a four-step counter is closer to the round
 * family the system already keeps for pins and clusters.
 *
 * The library dresses each step in three things and only one of them is the
 * circle: a 3px #f3f2ee ring (vue-form-wizard.min.css, .wizard-icon-circle),
 * an inner container rounded to a different 40%, and a 4px bar threaded behind
 * the row. Stacked, that is four edges competing to describe one number. The
 * ring and the bar go; the circle keeps the job.
 *
 * The ring is kept, at 2px and the faintest rule in the palette, because all
 * four have to READ as circles: with it transparent the steps you have not
 * reached were bare numbers floating in a row, and a counter whose unvisited
 * half is not drawn is not a counter. Faint is the whole point -- it is the
 * same #eeeeee as the unwalked connector below, so the path ahead of you is
 * one consistent, quiet value and the red is the only thing that speaks.
 *
 * The WIDTH also has to survive, which is the trap here: the library marks the
 * steps you have already finished with an inline `borderColor` on this element
 * (WizardStep.vue:66-70, stepCheckedStyle) and with nothing else, so `border:
 * 0` would leave that colour with nothing to paint and every completed step
 * would go invisible. Inline beats this rule, so finished steps take the red
 * and the rest keep the quiet one.
 */
.aa-wizard .wizard-icon-circle {
  border: 2px solid var(--aa-rule-2);
  border-radius: 50%;
  background-color: transparent;
}

.aa-wizard .wizard-icon-container {
  border-radius: 50%;
}

/* The library's own connector stays hidden. It is ONE bar threaded behind the
   whole row, drawn under the circles rather than between them, and filled to a
   percentage of the total width -- so its end lands wherever the arithmetic
   puts it, usually inside a circle, and it reads as a rule someone forgot to
   clip. What replaces it is below. */
.aa-wizard .wizard-progress-with-circle {
  display: none;
}

/* ---- the connector, per gap -------------------------------------------
 *
 * One segment per PAIR of circles rather than one bar behind all four. That
 * is the whole idea: a segment belongs to the gap it spans, so it starts and
 * stops at a circle's edge instead of passing underneath it, and "step 2 is
 * done" is a statement the drawing can make exactly rather than approximately.
 *
 * Geometry, all of it measured from the library's own CSS rather than guessed:
 * every `li` is `flex: 1` so the circles sit at equal centres, `li` is already
 * `position: relative` (.wizard-nav>li), the circle is 50px at stepSize="sm"
 * (.vue-form-wizard.sm .wizard-icon-circle) and its `a` is pushed down 3px
 * (.wizard-nav-pills>li>a), which puts the circle's midline at 3 + 25 = 28px.
 * Spanning `left: -50%; width: 100%` on every li AFTER the first therefore
 * reaches exactly from the previous circle's centre to this one's, and 35px of
 * inset at each end (25 radius + 10 breathing room) clears both circles.
 *
 * Two layers, because a track that is merely recoloured cannot be animated
 * from one end: `::before` is the unwalked path, `::after` the walked one,
 * revealed with scaleX from the left so the line grows in the direction the
 * visitor is travelling.
 */
.aa-wizard .wizard-nav-pills > li + li::before,
.aa-wizard .wizard-nav-pills > li + li::after {
  content: "";
  position: absolute;
  top: 28px;
  /* -50% + 35px puts the left end just past the previous circle; the width
     takes the same 35px off the far end, so the segment stops just short of
     this one. Both layers use the identical pair and stay in register. */
  left: calc(-50% + 35px);
  width: calc(100% - 70px);
  height: 2px;
  /* Under the circles, which carry the numbers. The `a` above is already
     positioned, so it wins the stack without needing a z-index of its own. */
  z-index: 0;
}

.aa-wizard .wizard-nav-pills > li + li::before {
  background: var(--aa-rule-2);
}

.aa-wizard .wizard-nav-pills > li + li::after {
  background: var(--aa-accent-fill);
  transform: scaleX(0);
  transform-origin: left center;
  /* Slower than a hover and faster than a page: long enough to be read as
     travel between two points, short enough that the next step is not waiting
     on it. Eased out so it arrives rather than stops. */
  transition: transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
}

/* A segment fills when the step on its RIGHT has been reached, because that is
   the moment the visitor actually crossed it. `-n+K` fills every gap up to the
   current step in one selector; the first `li` has no pseudo-elements, so the
   count lines up without an offset. */
.aa-wizard--step-2 .wizard-nav-pills > li:nth-child(-n + 2)::after,
.aa-wizard--step-3 .wizard-nav-pills > li:nth-child(-n + 3)::after,
.aa-wizard--step-4 .wizard-nav-pills > li:nth-child(-n + 4)::after {
  transform: scaleX(1);
}

@media (prefers-reduced-motion: reduce) {
  .aa-wizard .wizard-nav-pills > li + li::after {
    transition: none;
  }
}

.aa-wizard .wizard-tab-content {
  padding: 24px 0 0;
}

.aa-wizard .wizard-card-footer {
  padding: 0;
}

.aa-wizard-step {
  display: flex;
  flex-direction: column;
  gap: 24px;
  text-align: left;
}

.aa-wizard-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Lets a long value truncate inside a flex column instead of widening it. */
  min-width: 0;
}

.aa-wizard-label {
  font: 400 13px/18px "Open Sans", "Helvetica", arial, sans-serif;
  color: var(--aa-ink);
}

/* Red is reserved for the one action on the screen, so the required marker is
   ink rather than the usual red asterisk. It is aria-hidden: the inputs carry
   `required`, which is what a screen reader announces, and the v-selects are
   named in the "still needed" list under the button. */
.aa-wizard-required {
  color: var(--aa-ink-2);
}

.aa-wizard-help {
  margin: 0;
  font: 400 13px/18px "Open Sans", "Helvetica", arial, sans-serif;
  color: var(--aa-ink-2);
}

.aa-wizard-field {
  width: 100%;
  height: 44px;
  padding: 0 12px;
  /* #dddddd on white is 1.36:1, which does not meet the 3:1 WCAG 1.4.11 asks
     of a control's boundary. It is kept anyway, and deliberately: it is the
     site's control border, shared by the map page's search field, its four
     facets and its sort, and a wizard whose inputs did not match them would
     trade one defect for a worse one. Raising it is a single-token, site-wide
     decision, not this modal's to make on its own. The focus ring below is
     ink at 14.11:1, so the control a keyboard visitor is IN is never in doubt. */
  border: 1px solid var(--aa-rule);
  border-radius: 0;
  background: #ffffff;
  color: var(--aa-ink);
  font: 400 14px/21px "Open Sans", "Helvetica", arial, sans-serif;
  touch-action: manipulation;
}

.aa-wizard-textarea {
  height: auto;
  padding: 12px;
  resize: vertical;
}

.aa-wizard-field::placeholder {
  /* ink-2, not ink-3: a placeholder that carries an example has to be legible,
     and 3.3:1 is not. */
  color: var(--aa-ink-2);
  opacity: 1;
}

/* The replacement for `focus:outline-none`, which every field in this wizard
   carried with nothing put back -- so a keyboard visitor had no idea which of
   seven fields they were in. Matches MapComponent's convention of an explicit
   ring on :focus-visible only, so a mouse click does not draw one. */
.aa-wizard-field:focus-visible,
.aa-wizard-file:focus-visible,
.aa-wizard .vs__search:focus-visible {
  outline: 2px solid var(--aa-accent-fill);
  outline-offset: 2px;
}

.aa-wizard-file {
  font: 400 14px/21px "Open Sans", "Helvetica", arial, sans-serif;
  color: var(--aa-ink);
}

.aa-wizard-select .vs__dropdown-toggle {
  min-height: 44px;
  border: 1px solid var(--aa-rule);
  border-radius: 0;
  padding: 0 8px 4px;
}

.aa-wizard-select .vs__selected {
  border-radius: 0;
  border-color: var(--aa-rule);
  color: var(--aa-ink);
}

.aa-wizard-select .vs__dropdown-menu {
  border-radius: 0;
  border-color: var(--aa-rule);
}

.aa-wizard-map {
  width: 100%;
  height: 320px;
  border: 1px solid var(--aa-rule);
  background: var(--aa-surface);
}

.aa-wizard-preview {
  display: flex;
  justify-content: center;
}

.aa-wizard-preview-image {
  width: 240px;
  height: auto;
  max-width: 100%;
  border: 1px solid var(--aa-rule-2);
  object-fit: contain;
}

/* The system's inline result box, used for every failure and every explanation
   this wizard surfaces. Never red, never a second modal, always in the place
   the answer would have been. */
.aa-wizard-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  font: 400 13px/18px "Open Sans", "Helvetica", arial, sans-serif;
  color: var(--aa-ink);
  text-align: left;
}

/* The live region is always in the DOM so a reader has something to announce
   into; it only draws the box once it has something to say. */
.aa-wizard-note--filled {
  padding: 12px;
  border: 1px solid var(--aa-rule);
  background: var(--aa-surface);
}

.aa-wizard-note-icon {
  flex: none;
  color: var(--aa-ink-2);
}

.aa-wizard-footer {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding-top: 16px;
  border-top: 1px solid var(--aa-rule-2);
}

.aa-wizard-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
}

.aa-wizard-button {
  min-height: 44px;
  min-width: 44px;
  padding: 0 16px;
  border: 1px solid transparent;
  border-radius: 0;
  font: 600 14px/20px "Open Sans", "Helvetica", arial, sans-serif;
  cursor: pointer;
  touch-action: manipulation;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}

/* "The red appears once per screen, on the action." Next and Submit are that
   action; Cancel and Previous used to share the same filled red style and read
   exactly as loud. */
.aa-wizard-button--primary {
  background: var(--aa-accent-fill);
  border-color: var(--aa-accent-fill);
  color: #ffffff;
}

.aa-wizard-button--primary:hover:not(:disabled) {
  background: #b91c1c;
  border-color: #b91c1c;
}

/* Outlined in the same red, which is how this app has always written a
   secondary action: ArtworkFlagDialog.vue:38 and UploadPhoto.vue:41 are the
   two dialogs closest to this one and both pair `text-red-600 border-red-600`
   with a filled red primary, inverting to a red fill on hover. A grey-outlined
   button here would have been the only one of its kind in the product. */
.aa-wizard-button--quiet {
  background: #ffffff;
  border-color: var(--aa-accent-fill);
  color: var(--aa-accent-fill);
}

.aa-wizard-button--quiet:hover {
  background: var(--aa-accent-fill);
  color: #ffffff;
}

.aa-wizard-button:focus-visible {
  outline: 2px solid var(--aa-accent-fill);
  outline-offset: 2px;
}

.aa-wizard-button:disabled {
  background: var(--aa-surface);
  border-color: var(--aa-rule);
  /* ink-2 on surface measures 5.85:1 -- disabled, and still readable. WCAG
     exempts disabled controls from contrast, but "you cannot press this" is
     exactly the moment the label next to it has to be legible. */
  color: var(--aa-ink-2);
  cursor: not-allowed;
}

@media (prefers-reduced-motion: reduce) {
  .aa-wizard-close,
  .aa-wizard-button {
    transition: none;
  }
}
</style>
